"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const api_external_contract_js_1 = require("@tableau/api-external-contract-js");
const api_internal_contract_js_1 = require("@tableau/api-internal-contract-js");
const api_shared_js_1 = require("@tableau/api-shared-js");
const Dashboard_1 = require("../Models/Dashboard");
const Workbook_1 = require("../Models/Workbook");
const Worksheet_1 = require("../Models/Worksheet");
const DashboardContent_1 = require("../Namespaces/DashboardContent");
const Environment_1 = require("../Namespaces/Environment");
const Settings_1 = require("../Namespaces/Settings");
const UI_1 = require("../Namespaces/UI");
const WorkbookFormattingEnvUtil_1 = require("../Namespaces/WorkbookFormattingEnvUtil");
const WorksheetContent_1 = require("../Namespaces/WorksheetContent");
const RegisterAllExtensionsServices_1 = require("../Services/RegisterAllExtensionsServices");
const ServiceRegistryUtil_1 = require("../Services/ServiceRegistryUtil");
const BentonSansFontNameConstants_1 = require("./BentonSansFontNameConstants");
const ExtensionWorkbookImpl_1 = require("./ExtensionWorkbookImpl");
const LegacyInternalApiDispatcherHolder_1 = require("./LegacyInternalApiDispatcherHolder");
const SettingsImpl_1 = require("./SettingsImpl");
const TableauBookFontNameConstants_1 = require("./TableauBookFontNameConstants");
const UIImpl_1 = require("./UIImpl");
class ExtensionsImpl {
    initializeAsync(isExtensionDialog, contextMenuCallbacks) {
        if (!this._initializationPromise) {
            this._initializationPromise = new Promise((resolve, reject) => {
                var _a;
                const initOptions = { isAlpha: api_shared_js_1.ApiVersion.Instance.isAlpha, isExtensionDialog: isExtensionDialog };
                // First thing we want to do is check to see if there is a desktop dispatcher already registered for us
                if (LegacyInternalApiDispatcherHolder_1.LegacyInternalApiDispatcherHolder.hasDesktopApiDispatcherPromise(initOptions)) {
                    // Running in a pre-2019.3 desktop, use our legacy dispatcher promise
                    const desktopDispatcherPromise = LegacyInternalApiDispatcherHolder_1.LegacyInternalApiDispatcherHolder.getDesktopDispatcherPromise(initOptions);
                    (_a = desktopDispatcherPromise) === null || _a === void 0 ? void 0 : _a.then((dispatcherFactory) => this.onDispatcherReceived(dispatcherFactory, isExtensionDialog, contextMenuCallbacks)).then((openPayload) => {
                        resolve(openPayload);
                    }).catch((error) => {
                        reject(error);
                    });
                }
                else {
                    // We must be running in server, so we should try to kick of the server dispatcher bootstrapping
                    const onDispatcherReceivedCallback = this.onDispatcherReceived.bind(this);
                    api_shared_js_1.doCrossFrameBootstrap(window, api_internal_contract_js_1.INTERNAL_CONTRACT_VERSION, initOptions)
                        .then((factory) => {
                        return onDispatcherReceivedCallback(factory, isExtensionDialog, contextMenuCallbacks);
                    })
                        .then((openPayload) => {
                        resolve(openPayload);
                    })
                        .catch((error) => {
                        reject(error);
                    });
                }
            });
        }
        return this._initializationPromise;
    }
    createVizImageAsync(inputSpec) {
        const vizService = api_shared_js_1.ApiServiceRegistry.get(ServiceRegistryUtil_1.ExtensionsRegistryId).getService("viz-service" /* Viz */);
        return vizService.createVizImageAsync(inputSpec);
    }
    setClickThroughAsync(clickThroughEnabled) {
        const uiService = api_shared_js_1.ApiServiceRegistry.get(ServiceRegistryUtil_1.ExtensionsRegistryId).getService("UIService" /* UIService */);
        return uiService.setClickThroughAsync(clickThroughEnabled, this.extensionZoneId);
    }
    isDashboardExtensionLocator(locator) {
        const dashboardExtensionLocator = locator;
        if (!dashboardExtensionLocator.dashboardPath) {
            throw new api_shared_js_1.TableauError(api_external_contract_js_1.ErrorCodes.InternalError, 'Unexpected error during initialization.');
        }
        return true;
    }
    onDispatcherReceived(dispatcherFactory, isExtensionDialog, contextMenuFunctions) {
        let dispatcher = dispatcherFactory(api_internal_contract_js_1.INTERNAL_CONTRACT_VERSION);
        // Call to register all the services which will use the newly initialized dispatcher
        RegisterAllExtensionsServices_1.registerInitializationExtensionsServices(dispatcher);
        // Get the initialization service and initialize this extension
        const initializationService = api_shared_js_1.ApiServiceRegistry.get(ServiceRegistryUtil_1.ExtensionsRegistryId).getService("InitializationService" /* InitializationService */);
        const callbackMapKeys = contextMenuFunctions ? Object.keys(contextMenuFunctions) : [];
        return initializationService.initializeDashboardExtensionsAsync(isExtensionDialog, callbackMapKeys).then((result) => {
            // If we receive an invalid plaform version, this means that platform is runnning 1.4 or 2.1 and
            // doesn't pass the platform version to external. In this case we assume the platform version to be 1.9
            const platformVersion = result.extensionEnvironment.platformVersion
                ? result.extensionEnvironment.platformVersion
                : { major: 1, minor: 9, fix: 0 };
            // Wrap our existing dispatcher in a dispatcher that can downgrade/upgrade for an older platform.
            if (api_shared_js_1.VersionedExternalApiDispatcher.needsVersionConverter(platformVersion)) {
                dispatcher = new api_shared_js_1.VersionedExternalApiDispatcher(dispatcher, platformVersion);
            }
            // Registration of services must happen before initializing content and environment
            // Extensions doesn't need to pass in a registryId. By default, the service registry instance is associated with registryId=0.
            api_shared_js_1.registerAllSharedServices(dispatcher, ServiceRegistryUtil_1.ExtensionsRegistryId);
            RegisterAllExtensionsServices_1.registerAllExtensionsServices(dispatcher);
            if (result.extensionDashboardInfo && this.isDashboardExtensionLocator(result.extensionInstance.locator)) {
                this.dashboardContent = this.initializeDashboardContent(result.extensionDashboardInfo, result.extensionInstance.locator.dashboardPath);
                this.extensionZoneId = result.extensionDashboardInfo.extensionZoneId;
            }
            if (result.extensionWorksheetInfo) {
                this.worksheetContent = this.initializeWorksheetContent(result.extensionWorksheetInfo);
            }
            this.environment = new Environment_1.Environment(result.extensionEnvironment);
            this.settings = this.initializeSettings(result.extensionSettingsInfo);
            this.ui = new UI_1.UI(new UIImpl_1.UIImpl(isExtensionDialog));
            this.workbook = new Workbook_1.Workbook(new ExtensionWorkbookImpl_1.ExtensionWorkbookImpl());
            if (result.extensionEnvironment.workbookFormatting) {
                this.initializeTableauFonts(dispatcher, result.extensionEnvironment.workbookFormatting);
                this.applyAllFormatting(result.extensionEnvironment.workbookFormatting);
            }
            // After initialization has completed, setup listeners for the callback functions that
            // are meant to be triggered whenever a context menu item is clicked.
            this.initializeContextMenuCallbacks(contextMenuFunctions);
            // Also set up listeners for ExtensionStylesChangedEvent
            this.initializeWorkbookFormattingChangedEventCallback(dispatcher);
            // In the normal initialization case, this will be an empty string.  When returning from initializeAsync to the
            // developer, we just ingore that string.  In the case of initializing from an extension dialog, this string
            // is an optional payload sent from the parent extension.
            return result.extensionDialogPayload;
        });
    }
    applyAllFormatting(formattingModel) {
        if (!this._styleElement) {
            this._styleElement = document.createElement('style');
            this._styleElement.id = 'Tableau-Extension-Formatting';
            document.head.appendChild(this._styleElement);
        }
        if (!this._styleElement.sheet || !formattingModel) {
            return;
        }
        const stylesheet = this._styleElement.sheet;
        while (stylesheet.cssRules.length > 0) {
            stylesheet.deleteRule(stylesheet.cssRules.length - 1);
        }
        formattingModel.formattingSheets.map((currentFormattingSheet) => {
            this.applyFormattingSheetToStyleSheet(currentFormattingSheet, stylesheet);
        });
    }
    applyFormattingSheetToStyleSheet(currentFormattingSheet, stylesheet) {
        let styleInfo = '';
        const cssProperties = currentFormattingSheet.cssProperties;
        if (cssProperties.fontFamily) {
            styleInfo += 'font-family: ' + cssProperties.fontFamily.toString() + '; ';
        }
        if (cssProperties.fontSize) {
            styleInfo += 'font-size: ' + cssProperties.fontSize.toString() + '; ';
        }
        if (cssProperties.fontWeight) {
            styleInfo += 'font-weight: ' + cssProperties.fontWeight.toString() + '; ';
        }
        if (cssProperties.fontStyle) {
            styleInfo += 'font-style: ' + cssProperties.fontStyle.toString() + '; ';
        }
        if (cssProperties.textDecoration) {
            styleInfo += 'text-decoration: ' + cssProperties.textDecoration.toString() + '; ';
        }
        if (cssProperties.color) {
            styleInfo += 'color: ' + cssProperties.color + '; ';
        }
        const selector = '.' + currentFormattingSheet.classNameKey;
        const rule = selector + ' { ' + styleInfo + ' }';
        stylesheet.insertRule(rule, stylesheet.cssRules.length);
    }
    initializeWorkbookFormattingChangedEventCallback(dispatcher) {
        const notificationService = api_shared_js_1.ApiServiceRegistry.get(ServiceRegistryUtil_1.ExtensionsRegistryId).getService("notification-service" /* Notification */);
        // Unregister function not used since these notifications should be
        // observed for the full lifetime of the extension.
        notificationService.registerHandler(api_internal_contract_js_1.NotificationId.WorkbookFormattingChanged, () => true, (eventFormatting) => {
            if (eventFormatting) {
                this.initializeTableauFonts(dispatcher, eventFormatting);
                this.applyAllFormatting(eventFormatting);
            }
        });
    }
    initializeTableauFonts(dispatcher, workbookFormatting) {
        const fontNames = [...BentonSansFontNameConstants_1.SupportedBentonSansFontFaces, ...TableauBookFontNameConstants_1.SupportedTableauFontFaces];
        workbookFormatting.formattingSheets.forEach((formattingSheet) => {
            if (formattingSheet.cssProperties.fontFamily) {
                fontNames.push(formattingSheet.cssProperties.fontFamily);
            }
        });
        if (fontNames.length > 0) {
            const parameters = { [api_internal_contract_js_1.ParameterId.FontNameListItems]: fontNames };
            dispatcher
                .execute(api_internal_contract_js_1.VerbId.GetFonts, parameters)
                .then((response) => {
                this.loadFonts(response.result);
            })
                .catch();
        }
    }
    loadFonts(fonts) {
        fonts.forEach((fontNameAndInfo) => {
            if (TableauBookFontNameConstants_1.SupportedTableauFontFaces.includes(fontNameAndInfo.fontName)) {
                this.loadTableauFonts(fontNameAndInfo);
            }
            else if (BentonSansFontNameConstants_1.SupportedBentonSansFontFaces.includes(fontNameAndInfo.fontName)) {
                this.loadBentonSansFonts(fontNameAndInfo);
            }
            else {
                document.fonts.add(new FontFace(fontNameAndInfo.fontName, fontNameAndInfo.fontBinaryInfo));
            }
        });
    }
    initializeDashboardContent(info, sheetPath) {
        // sheets are always active for Extensions
        const sheetInfoImpl = new api_shared_js_1.SheetInfoImpl(info.name, api_external_contract_js_1.SheetType.Dashboard, { width: info.size.w, height: info.size.h }, undefined, true);
        const dashboardImpl = new api_shared_js_1.DashboardImpl(sheetInfoImpl, info.zones, sheetPath, ServiceRegistryUtil_1.ExtensionsRegistryId, null, info.activeDashboardObjectId);
        const dashboard = new Dashboard_1.Dashboard(dashboardImpl);
        return new DashboardContent_1.DashboardContent(dashboard);
    }
    initializeWorksheetContent(info) {
        const sheetInfoImpl = new api_shared_js_1.SheetInfoImpl(info.visualId.worksheet, api_external_contract_js_1.SheetType.Worksheet, { width: info.size.w, height: info.size.h }, undefined, // index property is embedding specific
        true);
        const worksheetImpl = new api_shared_js_1.WorksheetImpl(sheetInfoImpl, ServiceRegistryUtil_1.ExtensionsRegistryId, info.visualId, null, // parentDashboard is always null for worksheet in WorksheetContent
        null, // parentStoryPoint is only used for embedding
        info.backgroundColor, WorkbookFormattingEnvUtil_1.WorkbookFormattingEnvUtil.convertToWorkbookFormatting(info.formatting));
        const worksheet = new Worksheet_1.Worksheet(worksheetImpl);
        return new WorksheetContent_1.WorksheetContent(worksheet);
    }
    initializeSettings(settingsInfo) {
        const settingsImpl = new SettingsImpl_1.SettingsImpl(settingsInfo);
        return new Settings_1.Settings(settingsImpl);
    }
    initializeContextMenuCallbacks(contextMenuFunctions) {
        const notificationService = api_shared_js_1.ApiServiceRegistry.get(ServiceRegistryUtil_1.ExtensionsRegistryId).getService("notification-service" /* Notification */);
        // Unregister function not used since these notifications should be
        // observed for the full lifetime of the extension.
        notificationService.registerHandler(api_internal_contract_js_1.NotificationId.ContextMenuClick, () => {
            // Let through any context menu event, these are already filtered on api-core
            // based on the extension locator.
            return true;
        }, (event) => {
            // Execute the function associated with this context menu ID
            if (contextMenuFunctions) {
                if (!contextMenuFunctions[event.id]) {
                    throw new api_shared_js_1.TableauError(api_external_contract_js_1.ErrorCodes.InternalError, `Received unexpected context menu Id from event: ${event.id}`);
                }
                contextMenuFunctions[event.id]();
            }
        });
    }
    loadTableauFonts(fontNameAndInfo) {
        switch (fontNameAndInfo.fontName) {
            case TableauBookFontNameConstants_1.TableauBookFontFaceName:
                document.fonts.add(new FontFace(TableauBookFontNameConstants_1.TableauBookFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'normal', style: 'normal' }));
                break;
            case TableauBookFontNameConstants_1.TableauLightFontFaceName:
                document.fonts.add(new FontFace(TableauBookFontNameConstants_1.TableauLightFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'normal', style: 'normal' }));
                break;
            case TableauBookFontNameConstants_1.TableauRegularFontFaceName:
                document.fonts.add(new FontFace(TableauBookFontNameConstants_1.TableauRegularFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'normal', style: 'normal' }));
                break;
            case TableauBookFontNameConstants_1.TableauSemiBoldFontFaceName:
                document.fonts.add(new FontFace(TableauBookFontNameConstants_1.TableauSemiboldFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'normal', style: 'normal' }));
                document.fonts.add(new FontFace(TableauBookFontNameConstants_1.TableauRegularFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'bold', style: 'normal' }));
                break;
            case TableauBookFontNameConstants_1.TableauBoldFontFaceName:
                document.fonts.add(new FontFace(TableauBookFontNameConstants_1.TableauBoldFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'normal', style: 'normal' }));
                document.fonts.add(new FontFace(TableauBookFontNameConstants_1.TableauBoldFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'bold', style: 'normal' }));
                document.fonts.add(new FontFace(TableauBookFontNameConstants_1.TableauMediumFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'bold', style: 'normal' }));
                document.fonts.add(new FontFace(TableauBookFontNameConstants_1.TableauSemiboldFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'bold', style: 'normal' }));
                break;
            case TableauBookFontNameConstants_1.TableauMediumFontFaceName:
                document.fonts.add(new FontFace(TableauBookFontNameConstants_1.TableauMediumFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'normal', style: 'normal' }));
                document.fonts.add(new FontFace(TableauBookFontNameConstants_1.TableauLightFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'bold', style: 'normal' }));
                document.fonts.add(new FontFace(TableauBookFontNameConstants_1.TableauBookFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'bold', style: 'normal' }));
                break;
            default:
                throw new api_shared_js_1.TableauError(api_external_contract_js_1.ErrorCodes.InternalError, `'attempted to load unsupported font: ${fontNameAndInfo.fontName}.'`);
        }
    }
    loadBentonSansFonts(fontNameAndInfo) {
        switch (fontNameAndInfo.fontName) {
            case BentonSansFontNameConstants_1.BentonSansBookFontFaceName:
                document.fonts.add(new FontFace(BentonSansFontNameConstants_1.BentonSansFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'normal', style: 'normal' }));
                document.fonts.add(new FontFace(BentonSansFontNameConstants_1.BentonSansLowDPIFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: '100', style: 'normal' }));
                break;
            case BentonSansFontNameConstants_1.BentonSansLightFontFaceName:
                document.fonts.add(new FontFace(BentonSansFontNameConstants_1.BentonSansFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: '100', style: 'normal' }));
                break;
            case BentonSansFontNameConstants_1.BentonSansMediumFontFaceName:
                document.fonts.add(new FontFace(BentonSansFontNameConstants_1.BentonSansFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'bold', style: 'normal' }));
                document.fonts.add(new FontFace(BentonSansFontNameConstants_1.BentonSansLowDPIFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'bold', style: 'normal' }));
                break;
            case BentonSansFontNameConstants_1.BentonSansSemiDemiFontFaceName:
                document.fonts.add(new FontFace(BentonSansFontNameConstants_1.BentonSansLowDPIFontFamilyName, fontNameAndInfo.fontBinaryInfo, { weight: 'normal', style: 'normal' }));
                break;
            case BentonSansFontNameConstants_1.BentonSansBookItalicFontFaceName:
                document.fonts.add(new FontFace(BentonSansFontNameConstants_1.BentonSansFontFamilyName, fontNameAndInfo.fontBinaryInfo, { style: 'italic' }));
                break;
            case BentonSansFontNameConstants_1.BentonSansSemiDemiItalicFontFaceName:
                document.fonts.add(new FontFace(BentonSansFontNameConstants_1.BentonSansLowDPIFontFamilyName, fontNameAndInfo.fontBinaryInfo, { style: 'italic' }));
                break;
            default:
                throw new api_shared_js_1.TableauError(api_external_contract_js_1.ErrorCodes.InternalError, `'attempted to load unsupported font: ${fontNameAndInfo.fontName}.'`);
        }
    }
}
exports.ExtensionsImpl = ExtensionsImpl;
//# sourceMappingURL=ExtensionsImpl.js.map